<template>
  <el-col :span="24" id="new-bank-recon" v-loading.body="loading" element-loading-text="Loading...">
    <el-row type="flex" justify="start" align="middle" class="v-padded-s" id="new-bankrec-header">
      <el-col :span="14" class="h-padded left-aligned-col">
        <el-row type="flex" align="middle" justify="start">
          <h2 style="margin: 0; font-weight: 100; display: inline-block;">NEW BANK RECONCILIATION ENTRY</h2>
        </el-row>
      </el-col>
      <el-col :span="10" class="h-padded">
        <el-row type="flex" align="middle" justify="end">
          <el-button plain @click="goBack()" :disabled="loading || isSaving">Cancel</el-button>
          <el-button type="primary" :loading="exporting" :disabled="exporting" @click="exportCsvData">Export</el-button>
          <el-button type="primary" @click="createBankRec" :plain="!saveable" :loading="isSaving" :disabled="loading || !hasCreatePermission">
            Save
          </el-button>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" justify="start" align="start" :gutter="20" id="new-bankrec-body">
      <el-col :span="6" style="padding: 12px 0; border-right: 1px solid lightgrey;" v-if="form.start || form.end">
        <el-row type="flex" justify="center" align="middle" style="padding-right: 12px;">
          <BankRecForm
            :fields="fields"
            :form.sync="form"
            labelPosition="right"
            label-width="150px"
            :idPrefix="'a-'"
            @formChanged="formChanged"
            @selectMenuChanged="selectMenuChanged"
            @fieldFocusChanged="fieldFocusChanged"
          />
        </el-row>
        <el-row type="flex" align="middle" justify="center" style="padding: 24px 12px;">
          <BankRecSelectionSummary
            :selection="selection"
            :currency="currency"
            :getTabLabel="getTabLabel"
            :invertPayments="form.invert_payments"
          />
        </el-row>
      </el-col>
      <el-col :span="18" v-loading.body="loading" style="padding: 0;">
        <el-row v-if="reconcilablesCount" type="flex" align="start" :style="`height: ${tableHeight};`">
          <el-col>
            <BankRecTableHeader
              :reconcilables="reconcilables"
              :pageLimit="pageLimit"
              :currentPage="currentPage"
              :activeTabRows="activeTabRows"
              :activeTab="activeTab"
              :changeTabs="changeTabs"
              :changePage="changePage"
            />
            <el-row type="flex">
              <BankRecTable
                :ref="`${activeTab}Table`"
                ref="bankRecTable"
                :activeTab="activeTab"
                :paginatedData="paginatedData"
                :invertPayments="form.invert_payments"
                :pageLimit="pageLimit"
                :tableReady="tableReady"
                :parseTimestamp="parseTimestamp"
                :showLedgerItem="showLedgerItem"
                :handleSelectionChange="handleSelectionChange"
                :busy="busy"
                :hasCreatePermission="hasCreatePermission"
                @onNewGrid="handleGridUpdate"
              />
            </el-row>
          </el-col>
        </el-row>
        <el-row v-else type="flex" align="middle" justify="center" :style="`height: ${tableHeight}px;`">
          <el-col :span="12">
            <el-alert v-if="!loading" title="" type="info" style="margin-top: 24px;" :closable="false" show-icon>
            <span>No outstanding transactions.</span>
              <template>
                <slot slot-scope="scope">
                  <el-row type="flex" align="middle">
                    <el-col style="margin-left: 6px;" :span="24">
                      <el-row type="flex" justify="start">
                        <span style="font-weight: bold;">Nothing to display</span>
                      </el-row>
                      <el-row type="flex" justify="start" style="padding-top: 6px;">
                        No reconcilable lines or checks associated with this bank.
                      </el-row>
                    </el-col>
                  </el-row>
                </slot>
              </template>
            </el-alert>
          </el-col>
        </el-row>
        <el-dialog
          v-if="ledgerItem && ledgerModalVisible"
          :title="ledgerItem.meta_name"
          size="tiny"
          :show-close="false"
          :close-on-click-modal="false"
          :close-on-press-escape="false"
          :visible.sync="ledgerModalVisible">
          <el-row type="flex" justify="center" align="middle">
            <table id="related-transactions-table" cellspacing="0">
              <tr class="ledgeritem-row" v-for="prop in Object.keys(ledgerItem)" :key="`ledger_item_${prop}`">
                <td v-if="!['related', 'meta_name'].includes(prop)">
                  <el-row type="flex" align="middle" justify="end" style="font-weight: bold;">
                    {{ prop.charAt(0).toUpperCase() + prop.slice(1) }}:
                  </el-row>
                </td>
                <td v-if="!['related', 'meta_name'].includes(prop)">
                  <el-row type="flex" align="middle" justify="start">
                    {{ ledgerItem[prop] }}
                  </el-row>
                </td>
                <td v-if="prop === 'related'" valign="top">
                  <el-row type="flex" align="middle" justify="end" style="font-weight: bold;">
                    {{ `Related transaction${ledgerItem.related.length > 1 ? 's' : ''}` }}:
                  </el-row>
                </td>
                <td v-if="prop === 'related'" valign="top">
                  <a
                    v-if="ledgerItem.related.length > 1"
                    v-for="transaction in ledgerItem.related"
                    :key="`related_${transaction.type}_${transaction.id}`"
                    :href="transaction.path"
                    target="_blank"
                    style="display: block;">
                    {{ `${transaction.type} ${transaction.ledgerId}` }}
                  </a>
                  <a v-else :href="ledgerItem.related[0].path" target="_blank" style="display: block;">
                    {{ `${ledgerItem.related[0].type} ${ledgerItem.related[0].ledgerId}` }}
                  </a>
                </td>
              </tr>
            </table>
          </el-row>
          <span slot="footer" class="dialog-footer">
            <el-button type="primary" @click="ledgerModalVisible = false">Close</el-button>
          </span>
        </el-dialog>
      </el-col>
    </el-row>
  </el-col>
</template>

<script>
  /* eslint-disable camelcase */
  import BankRecForm from '@/components/Programs/BankRecon/BankRecForm.vue'
  import BankRecTable from '@/components/Programs/BankRecon/BankRecTable.vue'
  import BankRecTableHeader from '@/components/Programs/BankRecon/BankRecTableHeader.vue'
  import BankRecSelectionSummary from '@/components/Programs/BankRecon/BankRecSelectionSummary.vue'
  import { mapGetters } from 'vuex'
  import { bankRecMixins } from '@/Mixins/BankRecs'
  import { reportMixins } from '@/Mixins/Reports'
  import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'
  import { ManualCheckLoader } from './ManualCheckLoader'
  import { payload as bankActionPayload } from '@/components/RouteComponents/Banks/actionPayload'
  import moment from 'moment'

  export default {
    components: {
      BankRecForm,
      BankRecTable,
      BankRecTableHeader,
      BankRecSelectionSummary
    },
    mixins: [ bankRecMixins, reportMixins ],
    computed: {
      ...mapGetters({
        getBankById: 'getBankById'
      }),
      hasCreatePermission () {
        return this.$store.getters.checkIfUserHasRequiredPermissions(['programs.view_bankrecon', 'programs.post_bankrec'])
      }
    },
    async created () {
      this.loading = true
      let promises = []
      if (!this.$store.getters.getBankRecFields) { // Check if the options data exists in the store. If not, fetch it.
        promises.push(this.$store.dispatch('fetchBankRecOptions'))
      }
      const bank = this.getBankById(this.$route.query.bank)
      if (!(bank && Object.keys(bank).length)) {                                // Check if default bank already in store
        promises.push(this.$store.dispatch('fetchBanksWithQuery', { active: 'True' }))    // If not, fetch it
      }
      try {
        await Promise.all(promises)
        if (!(bank && Object.keys(bank).length)) {
          throw new Error('Unable to fetch default bank')
        }
        this.$store.dispatch('fetchBankStatementsByBank', bank.id)
          .then((response) => {
            const endDate = response && response.next && response.next.end
              ? response.next.end
              : moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD')
            promises = [                                                                        // Get reconcilable transactions
              this.$store.dispatch('fetchReconcilables', { type: 'lines/ap', query: { bank: bank.id, end: endDate } }),
              this.$store.dispatch('fetchReconcilables', { type: 'lines/je', query: { bank: bank.id, end: endDate } }),
              this.$store.dispatch('fetchReconcilables', { type: 'lines/pc', query: { bank: bank.id, end: endDate } }),
              this.$store.dispatch('fetchReconcilables', { type: 'checks', query: { bank: bank.id, end: endDate } }),
              this.$store.dispatch('fetchReconcilables', { type: 'payments', query: { bank: bank.id, end: endDate } })
            ]
            let dataLoader = new DataLoader(this.$store, [ bankActionPayload ])                       // Ensure that the banks are available
            dataLoader.loadData()
              .then(() => {
                Promise.all(promises)
                  .then(nextResponse => {
                    const manualCheckLoader = new ManualCheckLoader(this.$store, { id: null, apiResponse: nextResponse })
                    Promise.all([manualCheckLoader.fetchManualChecks(), this.$store.dispatch('fetchBankRecsWithQuery')])
                      .then(() => {
                        this.initializeData(nextResponse)
                        this.initForm = { ...this.form }
                        this.initializeFormFields()
                        this.prevFormState = { ...this.form }
                        this.getBankBalance()
                        this.loading = false
                      })
                  })
              })
          })
      } catch (e) {
        console.log(e)
        this.loading = false
      }
    },
    mounted () {
      const tableHeight = () => {
        const parentHeight = document.getElementById('new-bank-recon').offsetHeight
        const headerHeight = document.getElementById('new-bankrec-header').offsetHeight
        return parentHeight - headerHeight
      }
      this.tableHeight = tableHeight()
    }
  }
</script>

<style scoped>
  .focussed-btn {
    background-color: #04709d;
    cursor: default;
  }
  .unfocussed-btn {
    background-color: #fff;
    cursor: pointer;
  }
  #new-bankrec-body {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  #new-bankrec-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .v-padded-s {
    padding-top: 12px;
    padding-bottom: 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .selection-counter {
    color: #48576a;
    size: 14px;
    line-height: 1;
  }
  #related-transactions-table {
    width: 100%;
  }
  .ledgeritem-row td:first-child {
    font-weight: bold;
  }
  .ledgeritem-row td:nth-child(2) {
    width: 65%;
  }
  a, a:visited {
    text-decoration: none;
    color: rgb(0, 0, 238) !important;
  }
  #related-transactions-table td{
    padding: 6px;
    border: 1px solid lightgray;
  }
</style>



// WEBPACK FOOTER //
// src/components/Programs/BankRecon/New.vue